import React, { useLayoutEffect, useRef, useState, useMemo } from 'react';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { CTAButton, GMGenericButton } from '../../controls/ButtonBase/index';
import { useLocale, useLocalization } from '../../context/Localization/localization';
import { Heading } from '../../controls/Heading/Heading';
import { Tabs, Tab } from '../../controls/Tabs/UncontrolledTabs';
import { ScrollableBody } from '../Layout/ScrollableBody/ScrollableBody';
import css from './DisclaimerTabs.module.scss';
import { ImageHelper } from '../../controls/ImageHelper/ImageHelper';
import { TermsAndConditions } from './TermsAndConditions/TermsAndConditions';
import { useHideKioskBackground } from '../../context/Page/page';
import classNames from 'classnames';
import { useCart } from '../../context/cart';
import { Formik } from 'formik';
import { AppointmentDispatchTypes, useAppointmentDispatch, useDisclaimerInput, useEstimateSignature } from '../../context/Appointment/appointment';
import { SignatureComponent } from '../../controls/SignatureComponent/SignatureComponent';
import { SignatureSmallDisplay } from '../../controls/SignatureSmallDisplay/SignatureSmallDisplay';
import { PdfHelper } from '../../controls/PdfHelper/PdfHelper';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { nameof } from '../../helpers/nameof';
import { GMFormPriceField } from '../../controls/TextField';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMGenericIcon, GMRoundedIcon, GMSizedIcon } from '../../controls/Icon';
import { GMForm } from '../../controls/Form';

interface IProps {
    onProceed: () => void;
}

export const DisclaimerTabs: React.FC<IProps> = React.memo(({ onProceed }) => {
    useHideKioskBackground()();
    const {
        dealer: {
            primaryLanguage,
            consentToSignElectronicallyEnabled,
            retainOldPartsEnabled,
            roBackerEnabled,
            roBackerReviewRequired,
            stateEstimateEnabled,
            miscellaneousEnabled,
            roBackerImage,
            roBackerImageAltLang,
            stateEstimateMinimum,
            safeHarborWarningEnabled,
            localization: { retainOldPartsAdditionalText, miscellaneousDisclaimer },
        },
    } = useKioskState();
    const Localization = useLocalization();
    const enabledTabsHelper = [retainOldPartsEnabled, roBackerEnabled && roBackerReviewRequired, stateEstimateEnabled, miscellaneousEnabled];
    const retainOldPartsIndex = 0;
    const roBackerIndex = enabledTabsHelper.slice(0, 1)
        .filter((x) => x).length;
    const stateEstimateIndex = enabledTabsHelper.slice(0, 2)
        .filter((x) => x).length;
    const miscellaneousIndex = enabledTabsHelper.slice(0, 3)
        .filter((x) => x).length;
    const [enabledTabsCount] = React.useState(enabledTabsHelper.filter((x) => x).length);
    const [activeTabIndex, setActiveTabIndex] = React.useState(0);

    const { gaveConsentToSignElectronically, termsAcknowledged, estimateRequest, repairCost } = useDisclaimerInput();
    const { minEstCost } = useCart();
    const initialValues = { repairCost: repairCost !== undefined && repairCost !== 0 ? repairCost.toString() : minEstCost };
    const [selectionBoxValueDisplay, setSelectionBoxValueDisplay] = React.useState(initialValues.repairCost);
    const [repairCostHelper, setRepairCostHelper] = React.useState<string>('0');
    const appointmentDispatch = useAppointmentDispatch();

    const estimateSignature = useEstimateSignature();
    const signatureIsEmpty = estimateSignature === undefined;

    const locale = useLocale();
    const dateString = new Date()
        .toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });

    const signatureScrollToRef = useRef<HTMLDivElement>(null);
    const priceInputRef: React.MutableRefObject<any> = React.useRef();

    const url = useLocale() === primaryLanguage ? roBackerImage : roBackerImageAltLang;
    const isPdf = url?.endsWith('.pdf');
    const [popUpIsOpen, setPopUpIsOpen] = React.useState(false);

    const pageContainerRef = useRef<any>(null);
    const [pageWidth, setPageWidth] = useState(0);
    const [repairCostFocused, setRepairCostFocused] = useState<boolean>(false);
    const canProceed = useMemo<boolean>(() => {
        if (!stateEstimateEnabled) {
            return true;
        }

        if (
            !(consentToSignElectronicallyEnabled && gaveConsentToSignElectronically && signatureIsEmpty) &&
            !(!consentToSignElectronicallyEnabled && signatureIsEmpty) &&
            estimateRequest !== undefined
        ) {
            return true;
        }

        if (activeTabIndex !== stateEstimateIndex && !(miscellaneousEnabled && activeTabIndex === miscellaneousIndex)) {
            return true;
        }

        // no valid proceed conditions
        return false;
    }, [
        activeTabIndex,
        consentToSignElectronicallyEnabled,
        estimateRequest,
        gaveConsentToSignElectronically,
        miscellaneousEnabled,
        miscellaneousIndex,
        signatureIsEmpty,
        stateEstimateEnabled,
        stateEstimateIndex,
    ]);

    const isKiosk = isElectron();

    useLayoutEffect(() => {
        if (pageContainerRef.current) {
            setPageWidth(pageContainerRef.current.offsetWidth);
        }
    }, []);

    if (!(retainOldPartsEnabled || (roBackerEnabled && roBackerReviewRequired) || stateEstimateEnabled || miscellaneousEnabled)) {
        return null;
    }

    const tabAdvance = () => {
        if (!canProceed) {
            return;
        }

        if (activeTabIndex + 1 >= enabledTabsCount) {
            onProceed();
        }
        else {
            setActiveTabIndex(activeTabIndex + 1);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(_values, { setSubmitting }) => {
                setSubmitting(false);
                priceInputRef.current?.blur();
            }}
        >
            {({ values }) => (
                <GMForm
                    submitButton={
                        roBackerEnabled && roBackerReviewRequired && activeTabIndex === roBackerIndex ? (
                            <div className={css.termsScrollBodyBottom}>
                                <div className={css.termsCheckboxArea}>
                                    <TermsAndConditions
                                        termsAcknowledged={termsAcknowledged ?? false}
                                        setTermsAcknowledged={(val: boolean) => {
                                            appointmentDispatch({
                                                type: AppointmentDispatchTypes.TermsAcknowledged,
                                                payload: val,
                                            });
                                        }}
                                        setPopUpIsOpen={setPopUpIsOpen}
                                    />
                                </div>
                                <div className={css.buttonArea}>
                                    <GMButtonGroup
                                        buttons={[
                                            <CTAButton
                                                styleType={'primary'}
                                                onClick={tabAdvance}
                                                label={Localization.Proceed}
                                                title={Localization.ClickToProceedToNextStep}
                                                id={Localization.ClickToProceedToNextStep}
                                                ariaLabel={Localization.ClickToProceedToNextStep}
                                            />,
                                        ]}
                                    />
                                </div>
                            </div>
                        ) : !retainOldPartsEnabled || activeTabIndex !== retainOldPartsIndex ? (
                            <div className={css.termsScrollBodyBottom}>
                                <div className={css.buttonArea}>
                                    <GMButtonGroup
                                        buttons={[
                                            <CTAButton
                                                styleType={'primary'}
                                                onClick={tabAdvance}
                                                label={Localization.Proceed}
                                                title={Localization.ClickToProceedToNextStep}
                                                disabled={!canProceed && !repairCostFocused}
                                                id={Localization.ClickToProceedToNextStep}
                                                ariaLabel={Localization.ClickToProceedToNextStep}
                                            />,
                                        ]}
                                    />
                                </div>
                            </div>
                        ) : (
                            <></>
                        )
                    }
                >
                    <ScrollableBody
                        hideAccessibilityButton
                        heading={
                            <div className={css.headerMargin}>
                                <Heading primaryHeading={Localization.AlmostThere} leftAlign />
                            </div>
                        }
                        hideSpacer
                        ref={pageContainerRef}
                    >
                        <Tabs
                            activeTabIndex={activeTabIndex}
                            onTabChange={setActiveTabIndex}
                            noBottomMargin
                        >
                            {retainOldPartsEnabled && (
                                <Tab
                                    tabIndex={retainOldPartsIndex}
                                    label={Localization.Parts}
                                    hideKeyboard
                                    active={activeTabIndex === retainOldPartsIndex}
                                >
                                    <div className={css.parts}>
                                        <div className={css.partsTabMessage}>
                                            <h1>{Localization.WouldYouLikeToReceiveParts}</h1>
                                            <h2 className={css.noLowerMargin}>{retainOldPartsAdditionalText}</h2>
                                        </div>
                                        <div className={css.mustache} />
                                        <div className={css.spacer} />
                                        <GMButtonGroup
                                            alignment={'center'}
                                            buttons={[
                                                (
                                                    <CTAButton
                                                        styleType={'primary'}
                                                        onClick={() => {
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.RetainOldParts,
                                                                payload: false,
                                                            });
                                                            tabAdvance();
                                                        }}
                                                        label={Localization.No}
                                                        kioskOptions={{
                                                            square: true,
                                                            squareOptions: {
                                                                roundedIcon: (
                                                                    <GMRoundedIcon themeType={'primary'} roundedBackgroundSize={'large'}>
                                                                        <GMSizedIcon iconType={'No'} iconSize={'custom'} />
                                                                    </GMRoundedIcon>
                                                                ),
                                                            },
                                                        }}
                                                        title={Localization.ClickToRefuseReceivingParts}
                                                        id={Localization.ClickToRefuseReceivingParts}
                                                        ariaLabel={Localization.ClickToRefuseReceivingParts}
                                                    />
                                                ),
                                                (
                                                    <CTAButton
                                                        styleType={'secondary'}
                                                        onClick={() => {
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.RetainOldParts,
                                                                payload: true,
                                                            });
                                                            tabAdvance();
                                                        }}
                                                        label={Localization.Yes}
                                                        kioskOptions={{
                                                            square: true,
                                                            squareOptions: {
                                                                roundedIcon: (
                                                                    <GMRoundedIcon
                                                                        themeType={'primary'}
                                                                        allowInvertedPrimary
                                                                        roundedBackgroundSize={'large'}
                                                                    >
                                                                        <GMSizedIcon iconType={'Complete'} iconSize={'custom'} />
                                                                    </GMRoundedIcon>
                                                                ),
                                                            },
                                                        }}
                                                        title={Localization.ClickToReceiveParts}
                                                        id={Localization.ClickToReceiveParts}
                                                        ariaLabel={Localization.ClickToReceiveParts}
                                                    />
                                                ),
                                            ]}
                                        />
                                    </div>
                                </Tab>
                            )}
                            {roBackerEnabled && roBackerReviewRequired && (
                                <Tab
                                    tabIndex={roBackerIndex}
                                    label={Localization.TermsAndConditions}
                                    hideKeyboard
                                    active={activeTabIndex === roBackerIndex}
                                >
                                    <div className={css.termsHeader}>{Localization.PleaseReviewOurTermsAndConditions}</div>
                                    {isPdf ? (
                                        !popUpIsOpen && (
                                            <div>
                                                <PdfHelper
                                                    pageWidthPx={pageWidth}
                                                    src={url}
                                                    className={classNames(css.pdfContainer, isKiosk && !stateEstimateEnabled && css.withoutEstimateTab)}
                                                />
                                            </div>
                                        )
                                    ) : (
                                        <div className={classNames(css.imgDiv, isKiosk && !stateEstimateEnabled && css.withoutEstimateTab)}>
                                            <ImageHelper
                                                className={css.img}
                                                fullyQualified
                                                imageProps={{
                                                    src: url,
                                                    alt: Localization.TermsAndConditions,
                                                }}
                                            />
                                        </div>
                                    )}
                                </Tab>
                            )}
                            {stateEstimateEnabled && (
                                <Tab
                                    tabIndex={stateEstimateIndex}
                                    label={Localization.WrittenEstimate}
                                    active={activeTabIndex === stateEstimateIndex}
                                    hideKeyboard
                                >
                                    <div className={css.writtenEstimateTab}>
                                        <div className={isKiosk ? css.writtenEstimateLeftColumn : css.writtenEstimateLeftColumnMobile}>
                                            <h1 className={css.wouldYouLikeAWrittenEstimate}>{Localization.WouldYouLikeAWrittenEstimate}</h1>
                                            <div className={css.estimateButtonList}>
                                                <GMGenericButton
                                                    onClick={() => {
                                                        if (estimateRequest !== 'yes') {
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.EstimateRequest,
                                                                payload: 'yes',
                                                            });
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.EstimateSignature,
                                                                payload: undefined,
                                                            });
                                                        }
                                                    }}
                                                    title={Localization.ClickToRequestEstimate}
                                                    ariaChecked={estimateRequest === 'yes'}
                                                    id={Localization.ClickToRequestEstimate}
                                                    ariaLabel={Localization.ClickToRequestEstimate}
                                                >
                                                    <div className={classNames(css.estimateButton, estimateRequest === 'yes' ? css.selected : '')}>
                                                        {Localization.Yes}
                                                    </div>
                                                </GMGenericButton>
                                                <GMGenericButton
                                                    onClick={() => {
                                                        if (estimateRequest !== 'onlyIf') {
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.EstimateRequest,
                                                                payload: 'onlyIf',
                                                            });
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.EstimateSignature,
                                                                payload: undefined,
                                                            });

                                                            setTimeout(() => {
                                                                priceInputRef?.current?.focus();
                                                                setTimeout(() => {
                                                                    priceInputRef?.current?.select();
                                                                }, 1);
                                                            }, 1);
                                                        }
                                                    }}
                                                    title={Localization.ClickToRequestEstimateOnlyIf}
                                                    ariaChecked={estimateRequest === 'onlyIf'}
                                                    id={Localization.ClickToRequestEstimateOnlyIf}
                                                    ariaLabel={Localization.ClickToRequestEstimateOnlyIf}
                                                >
                                                    <div className={classNames(css.estimateButton, estimateRequest === 'onlyIf' ? css.selected : '')}>
                                                        {Localization.OnlyIfRepairsExceed}
                                                    </div>
                                                </GMGenericButton>
                                                <GMGenericButton
                                                    onClick={() => {
                                                        if (estimateRequest !== 'no') {
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.EstimateRequest,
                                                                payload: 'no',
                                                            });
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.EstimateSignature,
                                                                payload: undefined,
                                                            });
                                                        }
                                                    }}
                                                    title={Localization.ClickToRefuseEstimate}
                                                    ariaChecked={estimateRequest === 'no'}
                                                    id={Localization.ClickToRefuseEstimate}
                                                    ariaLabel={Localization.ClickToRefuseEstimate}
                                                >
                                                    <div className={classNames(css.estimateButton, estimateRequest === 'no' ? css.selected : '')}>
                                                        {Localization.No}
                                                    </div>
                                                </GMGenericButton>
                                            </div>
                                            <div className={css.repairCostDiv} style={estimateRequest !== 'onlyIf' ? { visibility: 'hidden' } : {}}>
                                                <GMFormPriceField
                                                    name={nameof<typeof values>('repairCost')}
                                                    id={nameof<typeof values>('repairCost')}
                                                    title={Localization.RepairCost}
                                                    label={Localization.RepairCost}
                                                    ariaLabel={Localization.RepairCost}
                                                    onChangeCallback={(value: string) => {
                                                        if (
                                                            value !== undefined &&
                                                            selectionBoxValueDisplay?.toString() !== value?.toString()
                                                        ) {
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.RepairCost,
                                                                payload: parseFloat(value),
                                                            });
                                                            setSelectionBoxValueDisplay(value !== '' ? value : '0');
                                                        }
                                                    }}
                                                    onFocusCallback={() => {
                                                        setRepairCostFocused(true);
                                                        const value = selectionBoxValueDisplay?.toString() ?? '0';
                                                        setRepairCostHelper(value !== '' ? value : '0');
                                                    }}
                                                    onBlurCallback={() => {
                                                        if (repairCostHelper !== selectionBoxValueDisplay.toString()) {
                                                            appointmentDispatch({
                                                                type: AppointmentDispatchTypes.EstimateSignature,
                                                                payload: undefined,
                                                            });
                                                        }
                                                        setRepairCostFocused(false);
                                                    }}
                                                    externalRef={priceInputRef}
                                                    hideKeyboardOnClickOut
                                                />
                                            </div>
                                        </div>
                                        <div className={isKiosk ? css.writtenEstimateRightColumn : css.writtenEstimateRightColumnMobile}>
                                            <div className={css.borderBoxEst}>
                                                <div className={css.topOfBox}>{Localization.YourSelection}</div>
                                                <div className={css.innerBox}>
                                                    <div className={css.pleasereadCarefullyLine}>{Localization.StateEstimatePart0}</div>
                                                    <div>{Localization.formatString(Localization.StateEstimatePart1, stateEstimateMinimum)}</div>
                                                    <div className={css.disclaimerLine}>
                                                        <div className={css.checkLine}>
                                                            {estimateRequest === 'yes' ? (
                                                                <div className={css.icon}>
                                                                    <GMSizedIcon iconSize={'custom'} iconType={'Complete'} />
                                                                </div>
                                                            ) : (
                                                                <>&#8203;</>
                                                            )}
                                                        </div>
                                                        {Localization.StateEstimatePart2}
                                                    </div>
                                                    <div className={css.disclaimerLine}>
                                                        <div className={css.checkLine}>
                                                            {estimateRequest === 'onlyIf' ? (
                                                                <div className={css.icon}>
                                                                    <GMSizedIcon iconSize={'custom'} iconType={'Complete'} />
                                                                </div>
                                                            ) : (
                                                                <>&#8203;</>
                                                            )}
                                                        </div>
                                                        {Localization.formatString(Localization.StateEstimatePart3, selectionBoxValueDisplay ?? '0')}
                                                    </div>
                                                    <div className={css.disclaimerLine}>
                                                        <div className={css.checkLine}>
                                                            {estimateRequest === 'no' ? (
                                                                <div className={css.icon}>
                                                                    <GMSizedIcon iconSize={'custom'} iconType={'Complete'} />
                                                                </div>
                                                            ) : (
                                                                <>&#8203;</>
                                                            )}
                                                        </div>
                                                        {Localization.StateEstimatePart4}
                                                    </div>
                                                    <div className={css.boxBottom}>
                                                        <div className={css.bottomLine}>
                                                            <div>{Localization.Signed}</div>
                                                            <div className={css.signedLine}>
                                                                <SignatureSmallDisplay />
                                                            </div>
                                                        </div>
                                                        <div className={css.bottomLine}>
                                                            <div>{Localization.Date}</div>
                                                            <div className={css.dateLine}>
                                                                <div className={css.shiftDate}>
                                                                    {gaveConsentToSignElectronically || !consentToSignElectronicallyEnabled ? dateString : ' '}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={css.selectionNote}>{Localization.ChangeYourSelectionByTapping}</div>
                                        </div>
                                    </div>
                                </Tab>
                            )}
                            {miscellaneousEnabled && (
                                <Tab
                                    tabIndex={miscellaneousIndex}
                                    label={Localization.Miscellaneous}
                                    active={activeTabIndex === miscellaneousIndex}
                                    addContainer
                                    hideKeyboard
                                >
                                    <div className={css.borderBoxMisc}>
                                        <div>
                                            {safeHarborWarningEnabled && (
                                                <>
                                                    <GMGenericIcon iconType={'HarborWarning'} />
                                                    <span className={css.warning}>{Localization.WarningHarbor}</span>
                                                </>
                                            )}
                                            {miscellaneousDisclaimer}
                                        </div>
                                    </div>
                                </Tab>
                            )}
                        </Tabs>
                        {stateEstimateEnabled && activeTabIndex === stateEstimateIndex && !repairCostFocused && (
                            <>
                                <div className={css.signBelow}>{Localization.SignBelowToSavePreferences}</div>
                                <SignatureComponent
                                    processing={false}
                                    signatureRequired={false}
                                    disclaimerPage
                                />
                                <div ref={signatureScrollToRef} />
                            </>
                        )}
                    </ScrollableBody>
                </GMForm>
            )}
        </Formik>
    );
});
